import { EditorState, Modifier } from 'draft-js';
import { OrderedSet } from 'immutable';
import { createRef, PureComponent } from 'react';

import { isMacOS } from 'my-core/user-agent-utils';

import { TEXT_COLORS } from '../constants';
import { FITBEditor } from '../plugins/fill-in-the-blank';
import { ImageEditor } from '../plugins/image';
import { LinkEditor } from '../plugins/link';
import { MergeVarsEditor } from '../plugins/merge-vars';
import { TooltipEditor } from '../plugins/tooltip';
import { VideoEditor } from '../plugins/video';
import { addEquation, toggleInlineStyle } from '../utils';
import CopyPasteManager from './CopyPasteManager';
import DropDownButton from './DropDownButton';
import RawDataInputManager from './RawDataInputManager';
import ToolbarButton from './ToolbarButton';
import ToolbarDivider from './ToolbarDivider';

import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';

import {
  faBold,
  faBrackets,
  faBracketsCurly,
  faDistributeSpacingVertical,
  faFunction,
  faImage,
  faInfoCircle,
  faItalic,
  faLink,
  faPalette,
  faSubscript,
  faSuperscript,
  faUnderline,
  faVideo,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export default class InlineStyleControls extends PureComponent {
  state = {};
  linkEditorAnchor = createRef();
  essayjackLinkEditorAnchor = createRef();
  fitbEditorAnchor = createRef();
  mergeVarsEditorAnchor = createRef();
  tooltipEditorAnchor = createRef();

  addEquation = block => () => addEquation(this.props.editorState, block, this.props.onChange);

  insertImage = blockImage => {
    this.setState({ blockImage });
    return this.openPlugin('image', true);
  };

  toggleInlineStyle = inlineStyle => {
    const { editorState, onChange } = this.props;
    const newEditorState = toggleInlineStyle(editorState, inlineStyle);
    onChange(newEditorState);
  };

  removeColorStyles = () => {
    const { editorState, onChange } = this.props;
    const selection = editorState.getSelection();
    let currentContent = editorState.getCurrentContent();
    if (selection.isCollapsed()) {
      const currentStyle = editorState.getCurrentInlineStyle();
      if (currentStyle) {
        onChange(
          EditorState.setInlineStyleOverride(
            editorState,
            currentStyle.filter(s => !s.startsWith('COLOR_')),
          ),
        );
      }
    } else {
      TEXT_COLORS.forEach(({ style }) => {
        currentContent = Modifier.removeInlineStyle(currentContent, selection, style);
      });
      onChange(EditorState.push(editorState, currentContent, 'change-inline-style'));
    }
  };

  openPlugin = (name, hideToolbar) => e => {
    const { onSetActive, onSetHidden } = this.props;
    e.preventDefault();
    onSetActive(true);
    onSetHidden(hideToolbar);
    this.setState({ openPlugin: name });
  };

  closePlugin = () => {
    this.setState({ openPlugin: null });
  };
  pluginClosed = () => {
    const { onFocus, onSetActive, onSetHidden } = this.props;
    onFocus();
    onSetActive(false);
    onSetHidden(false);
  };

  render() {
    const { editorState, plugins } = this.props;
    const contentState = editorState.getCurrentContent();
    const selection = editorState.getSelection();
    const block = contentState.getBlockForKey(selection.getStartKey());
    // after 'undo', the getCurrentInlineStyle will sometimes error out
    const currentStyle = block ? editorState.getCurrentInlineStyle() : OrderedSet();
    const currentColor = TEXT_COLORS.find(tc => currentStyle.has(tc.style));

    return (
      <>
        {plugins?.inlineStyles &&
          INLINE_STYLES.map((type, i) => (
            <ToolbarButton
              key={i}
              active={currentStyle.has(type.style)}
              onToggle={this.toggleInlineStyle}
              toggleKey={type.style}
              tooltip={type.label}
            >
              <FontAwesomeIcon icon={type.icon} />
            </ToolbarButton>
          ))}
        {plugins?.supSub && (
          <>
            <ToolbarButton
              active={currentStyle.has('SUP')}
              onToggle={this.toggleInlineStyle}
              toggleKey="SUP"
              tooltip="superscript"
            >
              <FontAwesomeIcon icon={faSuperscript} />
            </ToolbarButton>
            <ToolbarButton
              active={currentStyle.has('SUB')}
              onToggle={this.toggleInlineStyle}
              toggleKey="SUB"
              tooltip="subscript"
            >
              <FontAwesomeIcon icon={faSubscript} />
            </ToolbarButton>
          </>
        )}
        {(plugins?.inlineStyles || plugins?.supSub) && <ToolbarDivider />}
        {plugins?.colors && (
          <>
            <DropDownButton
              anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
              icon={<FontAwesomeIcon icon={faPalette} style={{ color: currentColor?.color || 'inherit' }} />}
              PaperProps={COLOR_PICKER_PROPS}
              tooltip="change text color"
              transformOrigin={{ horizontal: 'center', vertical: 'top' }}
            >
              <div className="colorButton" onMouseDown={this.removeColorStyles}>
                <svg height="26" viewBox="0 0 26 26" width="26">
                  <circle cx="13" cy="13" fill="currentColor" r="8" stroke="none" />
                  {!currentColor && <circle cx="13" cy="13" fill="none" r="11" stroke="currentColor" strokeWidth="2" />}
                </svg>
              </div>
              {TEXT_COLORS.filter(tc => !tc.old).map(({ color, hotkey, label, style }, i) => (
                <Tooltip key={i} title={`${label}${hotkey ? ` (⌘${hotkey.toUpperCase()})` : ''}`}>
                  <div className="colorButton" onMouseDown={() => this.toggleInlineStyle(style)}>
                    <svg height="26" viewBox="0 0 26 26" width="26">
                      <circle cx="13" cy="13" fill={color} r="8" stroke="none" />
                      {currentColor?.style === style && (
                        <circle cx="13" cy="13" fill="none" r="11" stroke={color} strokeWidth="2" />
                      )}
                    </svg>
                  </div>
                </Tooltip>
              ))}
            </DropDownButton>
            <ToolbarDivider />
          </>
        )}
        {this.renderPluginControls()}
      </>
    );
  }

  renderPluginControls() {
    const { activeInlineImage, editorState, onChange, onFocus, onSetActive, plugins } = this.props;
    const { blockImage, openPlugin } = this.state;
    const isMac = isMacOS();
    return (
      <>
        {plugins?.equation && (
          <DropDownButton icon={<FontAwesomeIcon icon={faFunction} />} menu tooltip="add equation">
            <Tooltip disableInteractive title={`${isMac ? 'Option' : 'Alt'} + '='`}>
              <MenuItem onMouseDown={this.addEquation(false)}>Inline equation</MenuItem>
            </Tooltip>
            <Tooltip disableInteractive title={`${isMac ? 'Option' : 'Alt'} + '-'`}>
              <MenuItem onMouseDown={this.addEquation(true)}>Block equation</MenuItem>
            </Tooltip>
          </DropDownButton>
        )}
        {plugins?.link && (
          <>
            <ToolbarButton buttonRef={this.linkEditorAnchor} onMouseDown={this.openPlugin('link')} tooltip="add link">
              <FontAwesomeIcon icon={faLink} />
            </ToolbarButton>
            <LinkEditor
              anchorEl={this.linkEditorAnchor.current}
              editorState={openPlugin === 'link' && editorState}
              onClose={this.closePlugin}
              onExited={this.pluginClosed}
              onUpdateEditorState={onChange}
              show={openPlugin === 'link'}
            />
          </>
        )}
        {plugins?.fillInTheBlank && (
          <>
            <ToolbarButton
              buttonRef={this.fitbEditorAnchor}
              onMouseDown={this.openPlugin('fitb')}
              tooltip="insert input field"
            >
              <FontAwesomeIcon icon={faBrackets} />
            </ToolbarButton>
            <FITBEditor
              anchorEl={this.fitbEditorAnchor.current}
              editorState={editorState}
              onClose={this.closePlugin}
              onExited={this.pluginClosed}
              onUpdateEditorState={onChange}
              show={openPlugin === 'fitb'}
            />
          </>
        )}
        {plugins?.mergeVars && (
          <>
            <ToolbarButton
              buttonRef={this.mergeVarsEditorAnchor}
              onMouseDown={this.openPlugin('merge-vars')}
              tooltip="insert merge variables"
            >
              <FontAwesomeIcon icon={faBracketsCurly} />
            </ToolbarButton>
            <MergeVarsEditor
              anchorEl={this.mergeVarsEditorAnchor.current}
              editorState={editorState}
              onClose={this.closePlugin}
              onExited={this.pluginClosed}
              onUpdateEditorState={onChange}
              options={plugins.mergeVars}
              show={openPlugin === 'merge-vars'}
            />
          </>
        )}
        {plugins?.tooltip && (
          <>
            <ToolbarButton
              buttonRef={this.tooltipEditorAnchor}
              onMouseDown={this.openPlugin('tooltip')}
              tooltip="add tooltip"
            >
              <FontAwesomeIcon icon={faInfoCircle} />
            </ToolbarButton>
            <TooltipEditor
              anchorEl={this.tooltipEditorAnchor.current}
              editorState={openPlugin === 'tooltip' && editorState}
              onClose={this.closePlugin}
              onExited={this.pluginClosed}
              onUpdateEditorState={onChange}
              open={openPlugin === 'tooltip'}
            />
          </>
        )}
        {['equation', 'link', 'essayjack_link', 'fillInTheBlank', 'mergeVars', 'tooltip'].some(p => plugins?.[p]) && (
          <ToolbarDivider />
        )}
        {plugins?.image && (
          <>
            <DropDownButton icon={<FontAwesomeIcon icon={faImage} />} menu tooltip="insert image">
              <MenuItem onMouseDown={e => this.insertImage(false)(e)}>Inline with text</MenuItem>
              <MenuItem onMouseDown={e => this.insertImage(true)(e)}>Break text</MenuItem>
            </DropDownButton>
            <ImageEditor
              blockImage={blockImage}
              editorState={openPlugin === 'image' && editorState}
              onChange={onChange}
              onClose={this.closePlugin}
              onExited={this.pluginClosed}
              onFocus={onFocus}
              show={openPlugin === 'image'}
            />
          </>
        )}
        {activeInlineImage && (
          <ToolbarButton onToggle={activeInlineImage.convert} tooltip="Convert to block image">
            <FontAwesomeIcon icon={faDistributeSpacingVertical} />
          </ToolbarButton>
        )}
        {plugins?.video && (
          <>
            <DropDownButton icon={<FontAwesomeIcon icon={faVideo} />} menu tooltip="add video">
              <MenuItem onMouseDown={this.openPlugin('video-library', true)}>My media</MenuItem>
              <MenuItem onMouseDown={this.openPlugin('video-youtube', true)}>YouTube video</MenuItem>
            </DropDownButton>
            <VideoEditor
              editorState={(openPlugin === 'video-youtube' || openPlugin === 'video-library') && editorState}
              mode={(openPlugin === 'video-youtube' || openPlugin === 'video-library') && openPlugin.substring(6)}
              onClose={this.closePlugin}
              onExited={this.pluginClosed}
              onUpdateEditorState={onChange}
            />
          </>
        )}
        {(plugins?.video || plugins?.image) && (plugins?.copyPaste || plugins?.rawLatexInput) && <ToolbarDivider />}
        {plugins?.copyPaste && (
          <>
            <CopyPasteManager editorState={editorState} onUpdateEditorState={onChange} />
            <ToolbarDivider />
          </>
        )}
        {plugins?.rawLatexInput && (
          <RawDataInputManager editorState={editorState} onSetActive={onSetActive} onUpdateEditorState={onChange} />
        )}
      </>
    );
  }
}

const INLINE_STYLES = [
  {
    icon: faBold,
    label: 'bold',
    style: 'BOLD',
  },
  {
    icon: faItalic,
    label: 'italic',
    style: 'ITALIC',
  },
  {
    icon: faUnderline,
    label: 'underline',
    style: 'UNDERLINE',
  },
  // {label: 'Monospace', style: 'CODE'},
];

const COLOR_PICKER_PROPS = {
  sx: {
    maxWidth: 304,
    '.colorButton': {
      display: 'inline-block',
      height: 38,
      padding: '6px',
      cursor: 'pointer',
      '&:hover': { backgroundColor: 'grey.200' },
    },
  },
};
